import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatAuthError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

export default function Login() {
  // 1. EXTRACT SUPABASE HERE: We added 'supabase' to the destructured context
  const { login, supabase } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back!");
      navigate(user.active_roadmap_id ? "/dashboard" : "/onboarding");
    } catch (err) {
      setError(formatAuthError(err));
    } finally { setLoading(false); }
  };

  // 2. UPDATE GOOGLE LOGIN: Use Supabase Auth instead of the legacy redirect
  const googleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    });
    
    if (error) {
      console.error("Google login error:", error);
      toast.error(error.message || "Failed to login with Google");
    }
  };

  return (
    <div className="min-h-screen bg-background grain flex flex-col">
      <div className="max-w-7xl w-full mx-auto px-5 h-16 flex items-center">
        <Link to="/" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white"><ArrowLeft className="w-4 h-4" /> Back</Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-5 pb-16">
        <div className="w-full max-w-md">
          <h1 className="font-heading text-3xl tracking-tighter font-extrabold">Welcome back</h1>
          <p className="text-zinc-400 mt-2 text-sm">Sign in to continue your roadmap.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-all" placeholder="you@example.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Password</label>
                <Link to="/reset-password" data-testid="forgot-password-link" className="text-xs text-primary hover:underline">
                  Forgot password?
                </Link>
              </div>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-all" placeholder="••••••••" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-400">{error}</p>}
            <button data-testid="login-submit" disabled={loading} type="submit"
              className="w-full bg-primary hover:bg-primary/90 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
            </button>
          </form>

          <div className="flex items-center gap-3 my-6"><div className="flex-1 h-px bg-white/10" /><span className="text-xs text-zinc-500">OR</span><div className="flex-1 h-px bg-white/10" /></div>

          <button data-testid="google-login-btn" onClick={googleLogin}
            className="w-full border border-white/15 hover:border-white/30 py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
          </button>

          <p className="text-sm text-zinc-400 mt-8 text-center">
            New here? <Link to="/signup" data-testid="go-signup" className="text-primary hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
